import {bidOfferRows, marketRows, previousDayName, previousReportParam, isEarlierReport} from '../charts/marketToday.js';
import {flowSource, regionFlows, weekBefore, weekRange} from '../charts/regionFlows.js';
import {balanceSplits, balanceCardLabel} from '../charts/balanceBarView.js';
import {notAuthorizedMessage} from '../services/not-authorized.js';

// How long one try at today's board may take, and the wait before the next try (as the app).
const TRY_TIMEOUT = 5000;
const RETRY_DELAY = 1000;

// The account lists on Overview. The bars read two of them: `aging` names the field they're
// kept in for that.
const ACCOUNT_LISTS = [
  {uri: 'org.eggs.trades_needing_payment', table: 'tradesNeedingPayment', aging: 'paymentDue'},
  {uri: 'org.eggs.seller_account_needs', table: 'sellerAccountNeeds', aging: 'needs'},
  {uri: 'org.eggs.outstanding_fees', table: 'fees'},
];

/** get_trades' answer, read defensively: [rows, "MM/DD/YY"] (a single arg arrives unwrapped). */
function parseBoard(res) {
  const args = (res && res.args) || (Array.isArray(res) ? [res] : []);
  return {
    data: Array.isArray(args[0]) ? args[0] : [],
    reportDate: typeof args[1] === 'string' ? args[1] : null,
  };
}

export default function MemberHomeCtrl($scope, DashBoardService, BidsOffersService, TradesService, progressBar, UserService, $wamp, $timeout, $filter, PreviousReportService, WeekTradesService) {

  $scope.info = DashBoardService.get();

  progressBar.add('dashboard');
  DashBoardService.load().then(function () {
    progressBar.remove('dashboard');
  });

  $scope.doRefresh = function () {
    progressBar.add('dashboard');
    DashBoardService.reload().then(function () {
      $scope.$broadcast('scroll.refreshComplete');
      progressBar.remove('dashboard');
    });
  };

  const userSub = UserService.subject.subscribe(function (user) {
    $scope.nominator = user.roles && user.roles.indexOf('nominator') >= 0;
  });

  let destroyed = false;

  // -- Market today and Where today's trades went -------------------------------------------
  //
  // Today's board is its own call, never TradesService.load('current'): that one subscribes
  // itself, polls for users with the refresh role, and pushes into the Trades board's shared
  // ReplaySubject and meta, so the board's cache would be overwritten.

  let today = null;        // {data, reportDate}: today's board once it has loaded
  let bos = null;          // {data, reportDate}: today's bids and offers once they have loaded
  let refused = {trades: null, bos: null};   // the backend's message for a board it won't show this login
  let previous = null;     // the previous report (report11), or null
  let previousSub = null;
  let flowSpan = 'day';    // the flow card's Day | Week, kept while the page is open
  let pastWeek = null;     // the 4 weekdays before today's board: (rows|null)[], null while loading
  let weekSub = null;
  let weekDay = null;      // the board day pastWeek was asked for

  /**
   * A board call, get_trades or get_bo ['current'], made until it answers. Each load() starts a
   * round (opening Overview, coming back to it, a reconnect). A failed try is tried again a second
   * later, until one succeeds or the page is left; a try that hasn't answered in 5 s counts as
   * failed, but its answer is still taken if it comes before another try's. `show` gets the raw
   * result. A refusal (the backend won't show this login the board: no trading access) ends the
   * round without another try, since the same login gets the same answer; `refuse` gets its
   * message. The next round asks again.
   */
  function boardLoader(procedure, show, refuse) {
    let retry = null;
    let round = 0;
    let answered = 0;      // the last round that got its answer
    let waiting = [];      // the round's tries still waiting for an answer (their Subscriptions)

    /**
     * Ends the tries still waiting: once the round has its answer, when a new round starts, and
     * when the page is left. A call that never answers (thruway.js drops a CALL made while the
     * socket is down, and doesn't send it again) would otherwise stay subscribed for the life of
     * the page. thruway.js sends a CANCEL for each, if the socket is open.
     */
    function drop() {
      const tries = waiting;
      waiting = [];
      tries.forEach(sub => sub.unsubscribe());
    }

    function attempt(r) {
      let over = false;    // counted as failed: it erred, or 5 s went by
      let ended = false;   // its call is over (answered, erred or completed): nothing to drop
      let sub = null;
      const failed = () => {
        if (over) {
          return;
        }
        over = true;
        $timeout.cancel(timer);
        if (!destroyed && r === round && answered !== r) {
          retry = $timeout(() => attempt(r), RETRY_DELAY, false);
        }
      };
      const end = () => {
        ended = true;
        waiting = waiting.filter(s => s !== sub);
      };
      const onRefused = message => {
        over = true;
        $timeout.cancel(timer);
        if (destroyed || r !== round || answered === r) {
          return;
        }
        answered = r;
        $timeout.cancel(retry);
        drop();
        refuse(message);
      };
      const timer = $timeout(failed, TRY_TIMEOUT, false);
      // $wamp.thruway.call, not $wamp.call: the raw result ({args}) as an RxJS 5 Observable, whose
      // subscription can be ended. A Promise can't be cancelled.
      sub = $wamp.thruway.call(procedure, ['current']).subscribe(res => {
        end();
        $timeout.cancel(timer);
        if (destroyed || r !== round || answered === r) {
          return;
        }
        answered = r;
        $timeout.cancel(retry);
        // The round's other tries. Not this one: it completes by itself, and ending it here,
        // before it has completed, would send a CANCEL for a call that has just answered.
        drop();
        show(res);
      }, err => {
        end();
        const message = notAuthorizedMessage(err);
        if (message) {
          onRefused(message);
        } else {
          failed();
        }
      }, () => {
        if (!ended) {      // completed without an answer
          end();
          failed();
        }
      });
      if (!ended) {
        waiting.push(sub);
      }
    }

    return {
      load() {
        $timeout.cancel(retry);
        drop();
        attempt(++round);
      },
      drop,
      stop() {
        $timeout.cancel(retry);
        drop();
      },
    };
  }

  // Today's bids and offers are their own call too, never BidsOffersService.load('current'),
  // which caches and polls for the Bids/Offers board.
  const boards = [
    boardLoader('org.eggs.get_trades', res => showToday(parseBoard(res)), message => showRefused('trades', message)),
    boardLoader('org.eggs.get_bo', res => showBos(parseBoard(res)), message => showRefused('bos', message)),
  ];

  /** Loads today's board and today's bids and offers (Today's activity switches between them). */
  function loadToday() {
    boards.forEach(board => board.load());
  }

  function dropWaiting() {
    boards.forEach(board => board.drop());
  }

  function showBos(board) {
    $scope.$applyAsync(() => {
      bos = board;
      refused = {...refused, bos: null};
      derive();
    });
  }

  /**
   * The backend's message for `board` ('trades' or 'bos'), which it won't show this login. What an
   * earlier round had of that board goes with it (a reconnect can come back without trading access):
   * no rows, change or flows from a board the login may no longer see.
   */
  function showRefused(board, message) {
    $scope.$applyAsync(() => {
      refused = {...refused, [board]: message};
      if (board === 'trades') {
        today = null;
        previous = null;
        pastWeek = null;
        weekDay = null;
        if (previousSub) {
          previousSub.unsubscribe();
          previousSub = null;
        }
        if (weekSub) {
          weekSub.unsubscribe();
          weekSub = null;
        }
      } else {
        bos = null;
      }
      derive();
    });
  }

  function showToday(board) {
    $scope.$applyAsync(() => {
      if (!today || today.reportDate !== board.reportDate) {
        previous = null;
      }
      today = board;
      refused = {...refused, trades: null};
      loadWeek();
      derive();
    });
    if (previousSub) {
      previousSub.unsubscribe();
    }
    previousSub = board.reportDate
      ? PreviousReportService.get(board.reportDate)
        .subscribe(p => $scope.$applyAsync(() => {
          previous = p;
          derive();
        }))
      : null;
  }

  /**
   * The flow card's past days, while Week is shown: asked for once per board day (WeekTradesService
   * keeps each day for the page's life, so after the first time they come from its cache). Asked
   * again for a new board day, and, when a day couldn't be had, the next time Week is chosen or the
   * board reloads (the days it has stay drawn meanwhile).
   */
  function loadWeek() {
    const day = today && today.reportDate;
    if (flowSpan !== 'week' || !day) {
      return;
    }
    if (day === weekDay && (!pastWeek || pastWeek.every(d => d))) {
      return;              // on its way, or all in
    }
    if (day !== weekDay) {
      pastWeek = null;     // another board day's week: "Loading the past week…" until this one's is in
    }
    weekDay = day;
    if (weekSub) {
      weekSub.unsubscribe();
    }
    weekSub = WeekTradesService.get(weekBefore(day)).subscribe(days => $scope.$applyAsync(() => {
      if (weekDay === day) {
        pastWeek = days;
        derive();
      }
    }));
  }

  /** The flow card's Day | Week switch. */
  $scope.showFlowSpan = span => {
    flowSpan = span === 'week' ? 'week' : 'day';
    loadWeek();
    derive();
  };

  /** The cards' model (the app's OverviewScreen): today's rows beside the previous report, and the flows. */
  function derive() {
    const comparable = today && previous && isEarlierReport(today.reportDate, previous.reportDate) ? previous : null;
    const src = flowSource(today ? today.data : null, comparable ? comparable.trades : null);
    const flows = src ? regionFlows(src.trades) : null;
    const flowDay = src ? src.day : 'today';
    // The past week: its days' rows and today's, as one more trades; every row is one trade.
    const week = flowSpan === 'week' && today && pastWeek
      ? {
        flows: regionFlows(pastWeek.reduce((all, d) => all.concat(d || []), []).concat(today.data || [])),
        range: weekRange(today.reportDate),
        missing: pastWeek.filter(d => !d).length,
      }
      : null;
    $scope.market = {
      today,
      // On both prices, so the card's Sale/Dock toggle switches without asking the controller.
      rows: {
        sale: today ? marketRows(today.data, comparable ? comparable.trades : null, 'sale') : [],
        dock: today ? marketRows(today.data, comparable ? comparable.trades : null, 'dock') : [],
      },
      previousDay: previousDayName(today && today.reportDate, comparable && comparable.reportDate),
      flows,
      flowDay,
      flowSpan,
      week,
      weekLoading: flowSpan === 'week' && !pastWeek,
      // What the flow card draws: the day's flows (no card without them), or the week's (the card
      // stays, with its switch, while they load and when there are none).
      flowsShown: flowSpan === 'week' ? week && week.flows : flows,
      flowDayShown: flowSpan === 'week' ? 'week' : flowDay,
      // The Trades board with the day's trades: where a colour of the flow card leads.
      flowReport: flowDay === 'previous' && comparable ? previousReportParam(comparable.reportDate) : 'current',
      // Today's bids and offers as rows (web-only), null until get_bo answers.
      bos: bos ? {bids: bidOfferRows(bos.data, 'B'), offers: bidOfferRows(bos.data, 'O')} : null,
      refused,
    };
  }

  // -- The account lists and the Payables / Receivables bars --------------------------------
  //
  // The tables start as [] and keep their rows while a reload is on its way; a list that fails
  // leaves its table []. The bars don't read that [] as "nothing late": to them a list that
  // failed is null (unknown).
  //
  // The bars are drawn from one round's balances and lists, never a mix. A round is each load of
  // Overview: opening it, coming back to it from one of its tabs, and a reconnect (app.js
  // reloads the dashboard then, and the '$wamp.open' handler below reloads the rest). A round's
  // balances are the first new info.balances after it starts; any already there are an earlier
  // load's. Until the latest round's balances and both lists are in, the bars keep the last
  // round's picture; before the first, the cards read their totals. (A card's total is
  // info.balances itself, so for that moment it can be newer than its bar.)

  let lists = {round: 0, paymentDue: null, needs: null};   // the latest round whose lists settled
  let listsRound = 0;       // the latest round
  let balancesRound = 0;    // the round the latest info.balances arrived in
  let drawn = false;        // the bars show a round's picture

  ACCOUNT_LISTS.forEach(list => {
    $scope[list.table] = [];
  });

  function loadAccountLists() {
    const r = ++listsRound;
    const next = {round: r, paymentDue: null, needs: null};
    let pending = ACCOUNT_LISTS.filter(list => list.aging).length;
    ACCOUNT_LISTS.forEach(list => {
      const settle = rows => {
        // Only the latest round touches the progress entry: the rounds share its key.
        if (destroyed || r !== listsRound) {
          return;
        }
        progressBar.remove(list.uri);
        $scope.$applyAsync(() => {
          $scope[list.table] = rows || [];
          if (list.aging) {
            next[list.aging] = rows;
            if (--pending === 0) {
              lists = next;
              deriveAging();
            }
          }
        });
      };
      // An earlier round may still be loading. Remove its entry first: that cancels its 10 s
      // timer, which add() would otherwise replace and leave to log "timed out".
      progressBar.remove(list.uri);
      progressBar.add(list.uri);
      $wamp.call(list.uri, []).then(res => settle(Array.isArray(res) ? res : null), () => settle(null));
    });
  }

  /** A total as its card shows it (the currency filter: "-$1,234.00"). */
  function shownTotal(n) {
    return n === undefined || n === null ? '' : $filter('currency')(n);
  }

  /** Both bars on one $ scale, and the cards' spoken labels, from one round's balances and lists. */
  function deriveAging() {
    const ready = lists.round === listsRound && balancesRound === listsRound;
    if (!ready && drawn) {
      return;               // the last round's picture stays until this one's is in
    }
    drawn = ready;
    const balances = $scope.info.balances;
    const splits = ready && balances ? balanceSplits(balances, lists.paymentDue, lists.needs) : null;
    $scope.aging = {
      payable: splits && splits.payable,
      receivable: splits && splits.receivable,
      scaleMax: splits ? splits.scaleMax : 0,
      payableLabel: balanceCardLabel('payables', splits && splits.payable, shownTotal(balances && balances.BuyerBal)),
      receivableLabel: balanceCardLabel('receivables', splits && splits.receivable, shownTotal(balances && balances.SellerBal)),
    };
  }

  $scope.$watch('info.balances', (balances, old) => {
    if (balances !== old) {       // the first call has what an earlier load left
      balancesRound = listsRound;
    }
    deriveAging();
  });

  // -- Loading and refreshing ----------------------------------------------------------------

  derive();
  loadAccountLists();
  loadToday();

  $scope.$on('$destroy', function () {
    destroyed = true;
    userSub.dispose();      // RxJS 4: the user stream outlives this controller
    if (previousSub) {
      previousSub.unsubscribe();
    }
    if (weekSub) {
      weekSub.unsubscribe();
    }
    boards.forEach(board => board.stop());
    ACCOUNT_LISTS.forEach(list => progressBar.remove(list.uri));
  });

  $scope.$on('$stateChangeSuccess', function (event, toState, toParams, fromState, fromParams) {
    if (toState.data) {
      $scope.currentTab = toState.data.selectedTab;
    }
    // Back on Overview from one of its own tabs this controller stays, so reload what Overview
    // shows (as the app does when Overview is shown again; there is no polling). Coming from
    // anywhere else creates a new controller, which loads it all anyway.
    if (toState.name === 'member.account' && fromState.name.indexOf('member.account.') === 0) {
      DashBoardService.load();
      loadAccountLists();
      loadToday();
    }
  });

  // After a reconnect app.js reloads the dashboard (the counts and balances): reload the rest of
  // Overview with it, so the bars get lists of the same round. Calls in flight when the socket
  // dropped never answer (thruway.js doesn't send them again), so this also replaces a round
  // they would have left unfinished. ui-router only creates this controller after the first
  // '$wamp.open' (the member state waits for loggedIn), so this hears reconnects only.
  $scope.$on('$wamp.open', function () {
    loadAccountLists();
    loadToday();
  });

  // A session that has died answers none of its calls, and the socket is down, so no CANCEL goes
  // out: let the board's waiting tries go now rather than when the next round starts.
  $scope.$on('$wamp.disconnected', dropWaiting);

}
